import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { OnboardingHeader } from '../../components/OnboardingHeader';
import { ProFooter } from '../../components/ProFooter';
import { getMe, updateMe } from '../../../lib/professional-api';
import { listServices, type ServiceItem } from '../../../lib/booking-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

type Row = {
  service: ServiceItem;
  enabled: boolean;
  // Empty string means "use catalog default" — we send null to the
  // backend so `custom_price` falls back to `services.base_price`.
  custom_price: string;
  custom_duration_minutes: string;
};

export default function ProServicesEditPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load the global service catalog + the pro's existing
  // professional_services rows in parallel, then merge so every
  // catalog entry has a row (enabled=false when not opted in).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [catalog, me] = await Promise.all([listServices(), getMe()]);
        if (cancelled) return;
        const proServices: Array<{
          service?: { id: string };
          custom_price?: number | null;
          custom_duration_minutes?: number | null;
        }> = me.info?.services ?? [];
        const proByServiceId = new Map(
          proServices.map((row) => [row.service?.id, row]),
        );
        const merged: Row[] = (catalog.data ?? [])
          .slice()
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((service) => {
            const existing = proByServiceId.get(service.id);
            return {
              service,
              enabled: !!existing,
              custom_price:
                existing?.custom_price != null
                  ? String(existing.custom_price)
                  : '',
              custom_duration_minutes:
                existing?.custom_duration_minutes != null
                  ? String(existing.custom_duration_minutes)
                  : '',
            };
          });
        setRows(merged);
      } catch (err) {
        if (!cancelled) {
          toast.error(apiErrorMessage(err, 'Could not load services.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Group by category for the section headings.
  const grouped = useMemo(() => {
    const out = new Map<string, { name: string; rows: Row[] }>();
    for (const row of rows) {
      const catId = row.service.category?.id ?? 'other';
      const catName = row.service.category?.name ?? 'Other';
      if (!out.has(catId)) out.set(catId, { name: catName, rows: [] });
      out.get(catId)!.rows.push(row);
    }
    return Array.from(out.values());
  }, [rows]);

  const toggle = (serviceId: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.service.id === serviceId
          ? { ...row, enabled: !row.enabled }
          : row,
      ),
    );
  };

  const updatePrice = (serviceId: string, value: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.service.id === serviceId
          ? { ...row, custom_price: value }
          : row,
      ),
    );
  };

  const updateDuration = (serviceId: string, value: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.service.id === serviceId
          ? { ...row, custom_duration_minutes: value }
          : row,
      ),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send enabled rows. Backend's upsertMany does a wipe-and-
      // reinsert per professional, so leaving a service out of the
      // payload also opts the pro out of it.
      const services = rows
        .filter((r) => r.enabled)
        .map((r) => {
          const priceNum = r.custom_price.trim() === ''
            ? null
            : Number(r.custom_price);
          const durationNum = r.custom_duration_minutes.trim() === ''
            ? null
            : parseInt(r.custom_duration_minutes, 10);
          return {
            service_id: r.service.id,
            custom_price: Number.isFinite(priceNum as number) ? priceNum : null,
            custom_duration_minutes: Number.isFinite(durationNum as number)
              ? durationNum
              : null,
            is_active: true,
          };
        });
      await updateMe({ services });
      toast.success('Services updated.');
      navigate('/professional/services');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save your services.'));
    } finally {
      setSaving(false);
    }
  };

  const ToggleSwitch = ({
    enabled,
    onToggle,
  }: {
    enabled: boolean;
    onToggle: () => void;
  }) => (
    <button
      onClick={onToggle}
      className={`relative w-[48px] h-[26px] rounded-full transition-colors duration-200 shrink-0 ${enabled ? 'bg-[#8e2a2f]' : 'bg-[#908e8e]/30'}`}
    >
      <div
        className={`absolute top-[3px] w-[20px] h-[20px] bg-white rounded-full shadow-sm transition-transform duration-200 ${enabled ? 'translate-x-[25px]' : 'translate-x-[3px]'}`}
      />
    </button>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <OnboardingHeader />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-6 md:py-12">
        <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[24px] md:text-[34px] text-center mb-2">
          Services
        </h1>
        <p className="text-center text-[#908e8e] text-[14px] mb-8 max-w-[640px] mx-auto">
          Turn on the services you offer. Leave the price and duration blank to
          use the platform default; enter a value to charge your own rate.
        </p>

        {loading ? (
          <p className="text-center text-sm text-[#908e8e]">Loading…</p>
        ) : grouped.length === 0 ? (
          <p className="text-center text-sm text-[#908e8e]">
            No services available right now.
          </p>
        ) : (
          <div className="space-y-12 max-w-[900px] mx-auto">
            {grouped.map((group) => (
              <div key={group.name}>
                <div className="bg-[#f2eee2] px-8 py-3 mb-4">
                  <h2 className="font-['DM_Sans',sans-serif] font-bold text-[16px] text-[#8e2a2f]">
                    {group.name}
                  </h2>
                </div>
                <div className="divide-y divide-[#eee] px-8">
                  {group.rows.map((row) => {
                    const basePrice = Number(row.service.base_price);
                    const perHour =
                      row.service.pricing_unit === 'PER_HOUR' ? '/hr' : '';
                    return (
                      <div
                        key={row.service.id}
                        className="flex flex-col md:flex-row md:items-center justify-between gap-3 py-4"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <ToggleSwitch
                            enabled={row.enabled}
                            onToggle={() => toggle(row.service.id)}
                          />
                          <div className="min-w-0">
                            <p
                              className={`font-['DM_Sans',sans-serif] font-bold text-[15px] tracking-[-0.1px] ${row.enabled ? 'text-[#3a3335]' : 'text-[#908e8e]'}`}
                            >
                              {row.service.name}
                            </p>
                            <p className="text-[12px] text-[#908e8e]">
                              Platform: £{basePrice.toFixed(2)}
                              {perHour} ·{' '}
                              {row.service.duration_minutes} min default
                            </p>
                          </div>
                        </div>

                        {row.enabled ? (
                          <div className="flex gap-2 shrink-0">
                            <label className="flex flex-col gap-1">
                              <span className="text-[11px] text-[#908e8e] uppercase tracking-wide">
                                Your price (£)
                              </span>
                              <input
                                type="number"
                                min={0}
                                step={0.01}
                                value={row.custom_price}
                                placeholder={basePrice.toFixed(2)}
                                onChange={(e) =>
                                  updatePrice(row.service.id, e.target.value)
                                }
                                className="w-[110px] bg-white border border-[#eee] rounded-lg px-3 py-2 text-[13px] font-bold text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                              />
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className="text-[11px] text-[#908e8e] uppercase tracking-wide">
                                Duration (min)
                              </span>
                              <input
                                type="number"
                                min={0}
                                step={5}
                                value={row.custom_duration_minutes}
                                placeholder={String(
                                  row.service.duration_minutes,
                                )}
                                onChange={(e) =>
                                  updateDuration(
                                    row.service.id,
                                    e.target.value,
                                  )
                                }
                                className="w-[110px] bg-white border border-[#eee] rounded-lg px-3 py-2 text-[13px] font-bold text-[#3a3335] outline-none focus:border-[#8e2a2f]"
                              />
                            </label>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={saving}
                className="h-[44px] px-6 rounded-[8px] font-bold text-[14px] text-[#908e8e] bg-[#3a3335]/[0.04] hover:bg-[#3a3335]/[0.08] transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="h-[44px] px-6 bg-[#8e2a2f] text-white rounded-[8px] font-bold text-[14px] hover:bg-[#7a2228] transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        )}
      </main>

      <ProFooter />
    </div>
  );
}
